import { z } from "zod";

import { assetSchema } from "./asset.schema";
import { positiveNumberSchema } from "./common.schema";

// =============================================================================
// Transaction Type Enum
// =============================================================================

export const transactionTypeSchema = z.enum([
  "buy",
  "sell",
  "exchange",
  "deposit",
  "withdrawal",
  "dividend",
  "interest",
  "currency_conversion",
]);

export const transactionDirectionSchema = z.enum(["buy", "sell"]);

export type TransactionType = z.infer<typeof transactionTypeSchema>;
export type TransactionDirection = z.infer<typeof transactionDirectionSchema>;

// =============================================================================
// Fee Schema
// =============================================================================

export const feeSchema = z.object({
  asset_id: z.string(),
  amount: z.number(),
});

export type Fee = z.infer<typeof feeSchema>;

// =============================================================================
// Record Schema (Transaction)
// =============================================================================

export const recordSchema = z.object({
  record_id: z.string(),
  book_id: z.string(),
  record_date: z.string(),
  type: z.string(),
  asset_id_buy: z.string().optional(),
  asset_id_sell: z.string().optional(),
  qty_sell: z.number().optional(),
  qty_buy: z.number().optional(),
  asset_id: z.string().optional(),
  amount: z.number().optional(),
  currency_asset_id: z.string().optional(),
  fees: z.array(feeSchema).optional(),
  description: z.string().optional(),
});

export type Record = z.infer<typeof recordSchema>;

// =============================================================================
// Record with Assets (enriched)
// =============================================================================

export const recordWithAssetsSchema = recordSchema.extend({
  buyAsset: assetSchema.optional(),
  sellAsset: assetSchema.optional(),
  asset: assetSchema.optional(),
  currencyAsset: assetSchema.optional(),
  feeAssets: z.record(z.string(), assetSchema).optional(),
});

export type RecordWithAssets = z.infer<typeof recordWithAssetsSchema>;

// =============================================================================
// Transaction Form Schemas (for validation)
// =============================================================================

// Base form fields
const baseTransactionFormSchema = z.object({
  record_date: z.union([z.string(), z.date()]),
  fees: z.array(feeSchema).optional(),
  description: z.string().optional(),
});

// Buy transaction
export const buyTransactionFormSchema = baseTransactionFormSchema.extend({
  type: z.literal("buy"),
  asset_id_buy: z.string().min(1, "Asset is required"),
  qty_buy: positiveNumberSchema,
  price_per_share: positiveNumberSchema,
  currency_asset_id: z.string().min(1, "Currency is required"),
});

// Sell transaction
export const sellTransactionFormSchema = baseTransactionFormSchema.extend({
  type: z.literal("sell"),
  asset_id_sell: z.string().min(1, "Asset is required"),
  qty_sell: positiveNumberSchema,
  price_per_share: positiveNumberSchema,
  currency_asset_id: z.string().min(1, "Currency is required"),
});

// Exchange transaction
export const exchangeTransactionFormSchema = baseTransactionFormSchema.extend({
  type: z.literal("exchange"),
  asset_id_buy: z.string().min(1, "Buy asset is required"),
  asset_id_sell: z.string().min(1, "Sell asset is required"),
  qty_buy: positiveNumberSchema,
  qty_sell: positiveNumberSchema,
});

// Deposit transaction
export const depositTransactionFormSchema = baseTransactionFormSchema.extend({
  type: z.literal("deposit"),
  asset_id: z.string().min(1, "Asset is required"),
  amount: positiveNumberSchema,
});

// Withdrawal transaction
export const withdrawalTransactionFormSchema = baseTransactionFormSchema.extend({
  type: z.literal("withdrawal"),
  asset_id: z.string().min(1, "Asset is required"),
  amount: positiveNumberSchema,
});

// Dividend transaction
export const dividendTransactionFormSchema = baseTransactionFormSchema.extend({
  type: z.literal("dividend"),
  asset_id: z.string().min(1, "Asset is required"),
  amount: positiveNumberSchema,
  currency_asset_id: z.string().min(1, "Currency is required"),
});

// Currency conversion transaction
export const currencyConversionFormSchema = baseTransactionFormSchema.extend({
  type: z.literal("currency_conversion"),
  asset_id_buy: z.string().min(1, "Buy currency is required"),
  asset_id_sell: z.string().min(1, "Sell currency is required"),
  qty_buy: positiveNumberSchema,
  qty_sell: positiveNumberSchema,
});

// Union of all transaction form schemas
export const transactionFormSchema = z.discriminatedUnion("type", [
  buyTransactionFormSchema,
  sellTransactionFormSchema,
  exchangeTransactionFormSchema,
  depositTransactionFormSchema,
  withdrawalTransactionFormSchema,
  dividendTransactionFormSchema,
  currencyConversionFormSchema,
]);

export type TransactionFormData = z.infer<typeof transactionFormSchema>;
export type BuyTransactionFormData = z.infer<typeof buyTransactionFormSchema>;
export type SellTransactionFormData = z.infer<typeof sellTransactionFormSchema>;
export type ExchangeTransactionFormData = z.infer<typeof exchangeTransactionFormSchema>;
export type DepositTransactionFormData = z.infer<typeof depositTransactionFormSchema>;
export type WithdrawalTransactionFormData = z.infer<typeof withdrawalTransactionFormSchema>;
export type DividendTransactionFormData = z.infer<typeof dividendTransactionFormSchema>;
export type CurrencyConversionFormData = z.infer<typeof currencyConversionFormSchema>;

// =============================================================================
// Simplified form schema for modals (non-discriminated, more flexible)
// =============================================================================

export const simpleTransactionFormSchema = z.object({
  type: transactionTypeSchema,
  record_date: z.union([z.string(), z.date()]),
  asset_id_buy: z.string().optional(),
  asset_id_sell: z.string().optional(),
  qty_buy: z.number().positive().optional(),
  qty_sell: z.number().positive().optional(),
  price_per_share: z.number().positive().optional(),
  asset_id: z.string().optional(),
  amount: z.number().positive().optional(),
  currency_asset_id: z.string().optional(),
  fees: z.array(feeSchema).optional(),
  description: z.string().optional(),
});

export type SimpleTransactionFormData = z.infer<typeof simpleTransactionFormSchema>;
